import { useState } from "react";
import { PageHeader, Disclaimer } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Phone, Video, MapPin, CheckCircle2, Upload, FileText, Clock, AlertTriangle, CalendarClock, ShieldAlert, Languages, ArrowRight } from "lucide-react";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

const VISA_TYPES = [
  "Student Visa (500)", "Graduate Visa (485)", "Skilled 189/190/491",
  "Employer Sponsored 482/186/494", "Partner / Family", "Visitor",
  "Refusal / ART matter", "Other",
];

// Languages spoken across our 4 Registered Migration Agents
const PREFERRED_LANGUAGES = [
  "English",
  "Bengali",
  "Hindi",
  "Nepali",
  "Punjabi",
  "Urdu",
  "Pashto",
  "Other (please specify in summary)",
];

const AGENTS = [
  { v: "any", l: "No preference — first available agent" },
  { v: "Shamima Yasmin Anwar (MARN 2619415 · IAA Licence No. 202603837)", l: "Shamima Yasmin Anwar · Director · Sydney" },
  { v: "Akram Khan (MARN 2418645)",           l: "Akram Khan · Perth" },
  { v: "Mandeep Bhullar (MARN 2518784)",      l: "Mandeep Bhullar · Melbourne" },
  { v: "Bina Gautam (MARN 2619194)",          l: "Bina Gautam · Sydney" },
];

export default function BookConsultation() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", visa_type: "",
    summary: "", consultation_type: "zoom", preferred_language: "English",
    preferred_agent: "any",
  });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { path: data.path, name: data.filename }]);
      toast.success(`${data.filename} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please select a date and time.");
      return;
    }
    if (!form.name || !form.email || !form.phone || !form.visa_type) {
      toast.error("Please complete required fields.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent statement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/consultations", {
        ...form,
        preferred_date: date.toISOString().split("T")[0],
        preferred_time: time,
        document_paths: docs.map((d) => d.path),
        document_filenames: docs.map((d) => d.name),
        consent: true,
        consent_timestamp: new Date().toISOString(),
      });
      setDone(true);
      toast.success("Booking received. We will confirm shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHeader eyebrow="Booking received" title="Thank you — your consultation request is in." subtitle="Yasmin will confirm your booking by email within 1 business day." />
        <section className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#C43A89] mx-auto mb-6" />
            <h2 className="font-display text-3xl text-[#29324A]">A confirmation will arrive shortly.</h2>
            <p className="mt-4 text-slate-600">Please prepare any relevant documents for our consultation.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="book-consultation-page">
      {/* ───────── HERO with embedded BOOKING FORM ───────── */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <span aria-hidden className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full bg-[#29324A]/30 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left — value props */}
            <div className="lg:col-span-5 xl:col-span-5">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
                <CalendarClock className="h-3 w-3" /> Book a Consultation
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight mt-4">
                Speak with a Registered Migration Agent.
              </h1>
              <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-5 max-w-xl">
                A 30-minute personalised session with one of our four MARA-registered agents — pathway recommendation, eligibility check, costs &amp; timeline, all in writing.
              </p>

              <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
                <Stat top="AUD $110" bottom="Fully deductible" />
                <Stat top="~30 min" bottom="Per session" />
                <Stat top="4 MARA agents" bottom="MARN registered" />
                <Stat top="7 languages" bottom="EN · BN · HI · NE · PA · UR · PS" />
              </div>

              <ul className="mt-7 space-y-2.5">
                {[
                  "Visa pathway recommendation tailored to your situation",
                  "Honest eligibility check against DHA criteria",
                  "Costs, timeline &amp; step-by-step process — in writing",
                  "Phone · Zoom · or in-person (Sydney / Melbourne / Perth)",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-slate-100">
                    <CheckCircle2 className="h-4.5 w-4.5 text-[#C43A89] mt-0.5 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: b }} />
                  </li>
                ))}
              </ul>

              {/* Availability disclaimer */}
              <div className="mt-7 bg-white/5 border-l-2 border-[#C43A89] backdrop-blur p-4">
                <div className="flex items-start gap-3">
                  <CalendarClock className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-slate-200 leading-relaxed">
                    <strong className="text-white">Please note:</strong> If your preferred agent is unavailable at the time you select, our migration team will contact you to confirm or reschedule to the next nearest slot.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — booking FORM card */}
            <div className="lg:col-span-7 xl:col-span-7" id="booking-form">
              <form onSubmit={submit} className="bg-white text-slate-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] border border-white/10 relative">
                <span aria-hidden className="absolute -top-3 left-6 bg-[#C43A89] text-white text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 shadow-md">Book here</span>

                <div className="p-6 md:p-8 space-y-5">
                  {/* Date + Time */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2 block">Preferred date</Label>
                      <div className="border border-slate-200 p-2">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                          data-testid="booking-calendar"
                        />
                      </div>
                      {date && (
                        <div className="mt-2 text-[11px] text-[#29324A] font-semibold" data-testid="selected-date">
                          {date.toLocaleDateString("en-AU", { weekday: "short", year: "numeric", month: "long", day: "numeric" })}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2 block">Preferred time (AEST)</Label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {TIME_SLOTS.map((t) => (
                          <button
                            key={t}
                            type="button"
                            data-testid={`time-slot-${t}`}
                            onClick={() => setTime(t)}
                            className={`py-2 text-[13px] font-semibold border transition-colors ${
                              time === t
                                ? "bg-[#29324A] text-white border-[#29324A]"
                                : "border-slate-200 text-[#29324A] hover:border-[#29324A]"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>

                      {/* Consultation type compact */}
                      <Label className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2 mt-5 block">Consultation type</Label>
                      <RadioGroup value={form.consultation_type} onValueChange={(v) => update("consultation_type", v)} className="space-y-1.5">
                        {[
                          { v: "phone", label: "Phone", Icon: Phone },
                          { v: "zoom", label: "Zoom video", Icon: Video },
                          { v: "in-person", label: "In-person", Icon: MapPin },
                        ].map(({ v, label, Icon }) => (
                          <label key={v} data-testid={`consult-type-${v}`} className="flex items-center gap-2.5 border border-slate-200 px-3 py-2 cursor-pointer hover:border-[#29324A] transition-colors text-[13px]">
                            <RadioGroupItem value={v} id={v} />
                            <Icon className="h-3.5 w-3.5 text-[#29324A]" />
                            <span className="font-medium text-[#29324A]">{label}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>

                  {/* Contact + visa */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name *"><Input data-testid="booking-name" required value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
                    <Field label="Email *"><Input data-testid="booking-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
                    <Field label="Phone *"><Input data-testid="booking-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                    <Field label="Visa type *">
                      <Select value={form.visa_type} onValueChange={(v) => update("visa_type", v)}>
                        <SelectTrigger data-testid="booking-visa-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{VISA_TYPES.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}</SelectContent>
                      </Select>
                    </Field>
                  </div>

                  {/* Agent + Language */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Preferred migration agent">
                      <Select value={form.preferred_agent} onValueChange={(v) => update("preferred_agent", v)}>
                        <SelectTrigger data-testid="booking-agent"><SelectValue /></SelectTrigger>
                        <SelectContent>{AGENTS.map((a) => (<SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>))}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Preferred language">
                      <Select value={form.preferred_language} onValueChange={(v) => update("preferred_language", v)}>
                        <SelectTrigger data-testid="booking-language">
                          <span className="flex items-center gap-2 text-sm text-[#29324A]">
                            <Languages className="h-4 w-4 text-[#29324A]" />
                            <SelectValue />
                          </span>
                        </SelectTrigger>
                        <SelectContent>{PREFERRED_LANGUAGES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}</SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Short summary of your matter *">
                    <Textarea data-testid="booking-summary" required rows={3} value={form.summary} onChange={(e) => update("summary", e.target.value)} placeholder="Briefly describe your situation and what you'd like to discuss." />
                  </Field>

                  {/* Doc upload — compact */}
                  <div>
                    <Label className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2 block">Upload documents (optional)</Label>
                    <label className="border border-dashed border-slate-300 hover:border-[#29324A] p-3 flex items-center gap-3 cursor-pointer transition-colors text-[13px]">
                      <Upload className="h-4 w-4 text-[#29324A] shrink-0" />
                      <span className="text-slate-600">Add CV, passport, refusal letter, etc. (PDF / DOC / image — up to 10 MB)</span>
                      <input data-testid="booking-doc-upload" type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={(e) => uploadDoc(e.target.files?.[0])} />
                    </label>
                    {uploading && <div className="text-xs text-slate-500 mt-1.5">Uploading…</div>}
                    {docs.length > 0 && (
                      <ul className="mt-1.5 space-y-1">
                        {docs.map((d, i) => (
                          <li key={i} className="flex items-center gap-2 text-[12px] text-emerald-700">
                            <FileText className="h-3.5 w-3.5" /> {d.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Consent */}
                  <ConsentCheckbox value={consent} onChange={setConsent} testid="booking-consent" showDisclaimer={false} />

                  {/* Submit */}
                  <Button data-testid="booking-submit-btn" type="submit" disabled={submitting || !consent} className="w-full bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white rounded-none py-5 font-semibold text-[15px] btn-shimmer disabled:opacity-50">
                    {submitting ? "Submitting…" : "Request Consultation"}
                  </Button>

                  {/* Slim fee note in card */}
                  <div className="pt-3 border-t border-slate-100 flex items-start gap-2 text-[10.5px] text-slate-500 leading-relaxed">
                    <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89] mt-0.5 shrink-0" />
                    <span>AUD $110 booking fee secures your session — fully deductible from any future engagement. Fees confirmed in writing before any paid work begins.</span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Fee structure */}
      <section data-testid="fee-structure" className="py-16 bg-[#F8F9FA] border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" />
            How it works
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#29324A] leading-tight mb-10 max-w-3xl">
            A transparent path from first call to engagement.
          </h2>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                n: "01",
                badge: "AUD $110",
                title: "Comprehensive Consultation Booking",
                desc: "Secure your detailed consultation. You'll share full profile information so we can review your circumstances thoroughly before we meet.",
              },
              {
                n: "02",
                badge: "TAILORED ADVICE",
                title: "Personalised Pathway Strategy",
                desc: "Based on your complete profile, we prepare tailored visa pathway options, eligibility analysis and clear next-step guidance.",
              },
              {
                n: "03",
                badge: "FULLY DEDUCTIBLE",
                title: "Engagement Credit",
                desc: "If you proceed with our professional services, your AUD $110 consultation fee is fully deducted from your final invoice — making it effectively free.",
              },
            ].map((s, i) => (
              <div key={i} data-testid={`fee-step-${i}`} className="bg-white border border-slate-200 p-6 relative">
                <div className="flex items-start justify-between mb-5">
                  <span className="font-display text-3xl text-[#C43A89] leading-none">{s.n}</span>
                  <span className="text-[10px] font-bold tracking-widest uppercase bg-[#29324A] text-white px-2.5 py-1">
                    {s.badge}
                  </span>
                </div>
                <h3 className="font-display text-lg text-[#29324A] leading-snug mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Payment options */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div data-testid="payment-stripe" className="bg-white border border-slate-200 p-6">
              <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-3">Option 1 · Pay Online</div>
              <h3 className="font-display text-xl text-[#29324A] mb-3">Card payment via Stripe</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Pay your AUD $110 consultation fee securely by card. Stripe checkout opens at the end of your booking form.
              </p>
              <span className="inline-block text-[10px] font-bold tracking-widest uppercase bg-[#F3F2F0] text-[#29324A] px-2.5 py-1">
                Coming soon — Stripe checkout
              </span>
            </div>
            <div data-testid="payment-bank" className="bg-[#29324A] text-white p-6 grain relative">
              <div className="relative">
                <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-3">Option 2 · Bank Transfer</div>
                <h3 className="font-display text-xl mb-4">Direct deposit (Australia)</h3>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between gap-3"><dt className="text-slate-400">Bank</dt><dd className="font-semibold">Commonwealth Bank of Australia</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-400">Account name</dt><dd className="font-semibold">StudyNet Pty Ltd</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-400">BSB</dt><dd className="font-semibold tabular-nums">062-016</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-400">Account number</dt><dd className="font-semibold tabular-nums">11373835</dd></div>
                </dl>
                <p className="mt-4 text-xs text-slate-300 leading-relaxed border-t border-white/10 pt-3">
                  After transfer, please email your payment screenshot/receipt to{" "}
                  <a href="mailto:yasmin@studynet.com.au" className="text-[#C43A89] underline font-semibold">yasmin@studynet.com.au</a>
                  {" "}and we will confirm your booking.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 italic max-w-3xl">
            Fees are confirmed in writing before any engagement. This process is conducted in accordance with the{" "}
            <a href="/documents/code-of-conduct.pdf" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">Migration Agents Code of Conduct</a>.
          </p>
        </div>
      </section>

      {/* Duration + Rescheduling */}
      <section data-testid="booking-duration-strip" className="py-12 md:py-14 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            <div data-testid="booking-duration-card" className="bg-[#F8F4FB] border border-[#29324A]/20 p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-[#29324A] text-white p-2.5 shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-1">Duration</div>
                  <div className="font-display text-xl text-[#29324A]">~30-minute session</div>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Our consultation sessions are scheduled for approximately <strong className="text-[#29324A]">30 minutes</strong> to discuss your migration or study pathway in detail.
              </p>
            </div>

            <div data-testid="booking-reschedule-card" className="bg-white border border-slate-200 p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-[#29324A] text-white p-2.5 shrink-0">
                  <CalendarClock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">Reschedule</div>
                  <div className="font-display text-xl text-[#29324A]">Need to change the time?</div>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                If you are unable to attend or wish to reschedule your appointment, kindly{" "}
                <a href="mailto:yasmin@studynet.com.au" className="text-[#29324A] underline font-semibold">contact us in advance</a>.
              </p>
            </div>
          </div>

          {/* CTA strip */}
          <div data-testid="booking-cta-strip" className="mt-8 bg-[#29324A] text-white p-6 md:p-7 grain relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Ready when you are</div>
              <h3 className="font-display text-xl md:text-2xl tracking-tight leading-snug">
                Schedule your consultation today and take the next step with confidence.
              </h3>
            </div>
            <a
              href="#booking-form"
              data-testid="booking-cta-anchor"
              className="relative shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer"
            >
              Schedule now <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* How Your Consultation Works */}
      <section data-testid="how-it-works" className="py-16 md:py-20 bg-[#F8F4FB] border-b border-slate-200 relative overflow-hidden">
        <div aria-hidden className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#C43A89]/8 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#29324A]/8 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
              What happens next
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
              How Your Consultation Works
            </h2>
            <p className="mt-4 text-base text-slate-700 leading-relaxed">
              A clear, professional process from the moment you book through to your personalised written advice — so you always know what to expect.
            </p>
          </div>

          {/* Step timeline */}
          <ol className="relative grid md:grid-cols-5 gap-6 md:gap-3">
            {/* connector line on md+ */}
            <span aria-hidden className="hidden md:block absolute top-7 left-[6%] right-[6%] h-px bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />

            {[
              {
                icon: CalendarClock,
                eyebrow: "Step 1",
                title: "Booking",
                desc: "Once your consultation is booked, our team will contact you by phone or email to collect some initial information about your background, goals and current situation.",
              },
              {
                icon: FileText,
                eyebrow: "Step 2",
                title: "Profile review",
                desc: "You may be asked to provide relevant details or documents before the consultation so our migration team can review your profile properly and prepare personalised guidance.",
              },
              {
                icon: Video,
                eyebrow: "Step 3",
                title: "Consultation",
                desc: "During the consultation you'll receive professional feedback on your profile, available pathways, eligibility, possible risks and recommended next steps — and have the chance to ask questions.",
              },
              {
                icon: ShieldAlert,
                eyebrow: "Step 4",
                title: "Written advice",
                desc: "After the consultation, a written letter of advice will be provided based on your circumstances and the service requested.",
              },
              {
                icon: ArrowRight,
                eyebrow: "Step 5",
                title: "Next steps",
                desc: "If you decide to proceed with our services, the consultation fee may be adjusted toward your professional service fees, depending on the visa type and engagement.",
              },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={i}
                  data-testid={`how-step-${i}`}
                  className="relative bg-white border border-slate-200 p-5 md:p-6 hover:border-[#C43A89] transition-colors group flex flex-col"
                >
                  <span aria-hidden className="absolute top-0 left-0 h-0.5 w-8 bg-[#C43A89] group-hover:w-full transition-all duration-500" />
                  <div className="relative z-10 h-14 w-14 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-lg mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1">
                    {step.eyebrow}
                  </div>
                  <div className="font-display text-lg text-[#29324A] leading-snug mb-2">{step.title}</div>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{step.desc}</p>
                </li>
              );
            })}
          </ol>

          {/* Notes strip */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="bg-white border-l-4 border-[#29324A] p-4 flex items-start gap-3">
              <Clock className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
              <div className="text-[13px] text-slate-700 leading-relaxed"><strong className="text-[#29324A]">Approx 30 minutes</strong> per session.</div>
            </div>
            <div className="bg-white border-l-4 border-[#C43A89] p-4 flex items-start gap-3">
              <Video className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
              <div className="text-[13px] text-slate-700 leading-relaxed">Available via <strong className="text-[#29324A]">phone, Zoom or in-person</strong>.</div>
            </div>
            <div className="bg-white border-l-4 border-[#29324A] p-4 flex items-start gap-3">
              <CalendarClock className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
              <div className="text-[13px] text-slate-700 leading-relaxed">Need to reschedule? <strong className="text-[#29324A]">Please contact us in advance.</strong></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Your Consultation Includes */}
          <div data-testid="consultation-includes">
            <div className="max-w-3xl">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
                What you&apos;ll get
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
                Your Consultation Includes
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Our consultation is designed to provide personalised guidance based on your unique migration or education goals.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "Your goals & background assessment", desc: "We listen, then map your profile against current migration & education options." },
                { title: "Visa type recommendation", desc: "A clear recommendation on the most suitable visa pathway for your circumstances." },
                { title: "Eligibility check", desc: "An honest, evidence-based eligibility review against Department of Home Affairs criteria." },
                { title: "Step-by-step process overview", desc: "Exactly what happens from application preparation to lodgement and decision." },
                { title: "Cost & timeline breakdown", desc: "Government fees, professional fees and realistic processing timelines — all written down." },
                { title: "Tailored migration strategy", desc: "A personalised next-step plan you can take away — whether you proceed with us or not." },
              ].map((item, i) => (
                <div
                  key={i}
                  data-testid={`includes-item-${i}`}
                  className="group relative bg-white border border-slate-200 hover:border-[#C43A89] p-5 md:p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(196,58,137,0.25)]"
                >
                  <span aria-hidden className="absolute top-0 left-0 h-0.5 w-10 bg-[#C43A89] group-hover:w-full transition-all duration-300" />
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-md">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-base md:text-lg text-[#29324A] leading-snug mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-[13px] text-slate-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Legal Disclaimer */}
          <div data-testid="booking-legal-disclaimer" className="mt-12 bg-[#F8F9FA] border-l-4 border-[#29324A] p-6 md:p-7 flex items-start gap-4">
            <div className="bg-[#29324A]/10 p-2.5 shrink-0">
              <ShieldAlert className="h-5 w-5 text-[#29324A]" />
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-2">Legal Disclaimer</div>
              <p className="mb-2">
                <strong className="text-[#29324A]">StudyNet Pty Ltd</strong> is a private education and migration consultancy and is <strong className="text-[#29324A]">not affiliated with the Australian Government</strong>.
              </p>
              <p>
                All visa applications are assessed independently by the <a href="https://immi.homeaffairs.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">Department of Home Affairs</a> under current migration law and policy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function Stat({ top, bottom }) {
  return (
    <div className="bg-white/5 border border-white/10 backdrop-blur p-3">
      <div className="font-display text-lg text-white leading-tight">{top}</div>
      <div className="text-[10px] tracking-wider uppercase text-[#C43A89] mt-1">{bottom}</div>
    </div>
  );
}
